// src/components/TransitionOverlay.jsx
//
// Screen-change "vault doors". Pages flip MainContext.screenLoading to true
// before navigating and back to false once the next screen is ready. This
// component turns those flips into one coordinated sequence:
//
//   open → closing → closed → unlocking → opening → open
//
// Exactly one instance should be mounted (App.js). Every phase change goes
// through a single reducer-like setter, so overlapping triggers can never
// run two animations at once.
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import AudioManager from "../@shared/utils/AudioManager";
import { MainContext } from "../App";

const doorImage = require("../@shared/assets/web-door-image.png");
const lockImage = require("../@shared/assets/web-door-lock.png");

const CLOSE_MS = 420;
const UNLOCK_MS = 620;
const OPEN_MS = 560;
// Keep the doors shut at least this long so quick loads still read as a
// deliberate transition rather than a flicker.
const MIN_CLOSED_MS = 320;
// If a screen never reports it's ready, open anyway instead of trapping the
// player behind the doors.
const FAILSAFE_MS = 10000;

const ease = {
  in: [0.7, 0, 0.84, 0],
  out: [0.16, 1, 0.3, 1],
};

const panelStyle = {
  position: "absolute",
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  willChange: "transform",
};

export default function TransitionOverlay() {
  const { screenLoading } = useContext(MainContext);
  const reduceMotion = useReducedMotion();

  const [phase, setPhase] = useState("open");
  const phaseRef = useRef("open");
  const closedAtRef = useRef(0);
  const wantsOpenRef = useRef(!screenLoading);
  const timersRef = useRef([]);

  const go = useCallback((next) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  const later = (fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  };

  const beginUnlock = useCallback(() => {
    if (phaseRef.current !== "closed") return;
    clearTimers();
    go("unlocking");
    later(() => {
      go("opening");
      later(() => go("open"), OPEN_MS);
    }, UNLOCK_MS);
  }, [go]);

  // Open once the doors have been shut long enough.
  const scheduleOpen = useCallback(() => {
    if (phaseRef.current !== "closed") return;
    const shutFor = Date.now() - closedAtRef.current;
    const wait = Math.max(0, MIN_CLOSED_MS - shutFor);
    later(beginUnlock, wait);
  }, [beginUnlock]);

  useEffect(() => {
    wantsOpenRef.current = !screenLoading;
    const current = phaseRef.current;

    if (screenLoading) {
      // Already closed/closing: nothing to restart.
      if (current === "closing" || current === "closed") return;
      clearTimers();
      AudioManager.playSFX("doorTransition");
      go("closing");
      later(() => {
        closedAtRef.current = Date.now();
        go("closed");
        if (wantsOpenRef.current) {
          scheduleOpen();
        } else {
          later(() => {
            if (phaseRef.current === "closed") beginUnlock();
          }, FAILSAFE_MS);
        }
      }, CLOSE_MS);
      return;
    }

    // screenLoading went false.
    if (current === "closed") {
      clearTimers();
      scheduleOpen();
    }
    // If still closing, the close timer above will see wantsOpenRef and
    // schedule the open itself. Open/opening/unlocking need nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenLoading]);

  useEffect(() => () => clearTimers(), []);

  const doorsShut = phase === "closing" || phase === "closed" || phase === "unlocking";
  const visible = phase !== "open";

  if (reduceMotion) {
    return (
      <motion.div
        aria-hidden
        initial={false}
        animate={{ opacity: doorsShut ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "#07030f",
          pointerEvents: doorsShut ? "auto" : "none",
        }}
      />
    );
  }

  const doorTransition = doorsShut
    ? { duration: CLOSE_MS / 1000, ease: ease.in }
    : { duration: OPEN_MS / 1000, ease: ease.out };

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        overflow: "hidden",
        // Block taps only while the doors are covering the screen.
        pointerEvents: visible ? "auto" : "none",
        visibility: visible ? "visible" : "hidden",
      }}
    >
      {/* Top door (artwork flipped) */}
      <motion.div
        initial={false}
        animate={{ y: doorsShut ? "0%" : "-100%" }}
        transition={doorTransition}
        style={{ ...panelStyle, top: 0 }}
      >
        <img
          src={doorImage}
          alt=""
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "rotate(180deg)",
          }}
        />
      </motion.div>

      {/* Bottom door */}
      <motion.div
        initial={false}
        animate={{ y: doorsShut ? "0%" : "100%" }}
        transition={doorTransition}
        style={{ ...panelStyle, top: 0 }}
      >
        <img
          src={doorImage}
          alt=""
          draggable={false}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </motion.div>

      {/* Seam glow where the doors meet */}
      <motion.div
        initial={false}
        animate={{
          opacity: phase === "closed" || phase === "unlocking" ? 1 : 0,
          scaleX: phase === "closed" || phase === "unlocking" ? 1 : 0.2,
        }}
        transition={{ duration: 0.35, ease: ease.out }}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: 3,
          marginTop: -1.5,
          background:
            "linear-gradient(90deg, rgba(255,211,106,0) 0%, rgba(255,233,168,0.95) 50%, rgba(255,211,106,0) 100%)",
          boxShadow: "0 0 24px 4px rgba(255,211,106,0.55)",
          pointerEvents: "none",
        }}
      />

      {/* Unlock flash */}
      <motion.div
        initial={false}
        animate={
          phase === "unlocking"
            ? { opacity: [0, 0, 0.85, 0], scale: [0.4, 0.4, 1.4, 1.8] }
            : { opacity: 0, scale: 0.4 }
        }
        transition={
          phase === "unlocking"
            ? { duration: UNLOCK_MS / 1000, times: [0, 0.45, 0.6, 1], ease: "easeOut" }
            : { duration: 0.2 }
        }
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "min(60vw, 420px)",
          height: "min(60vw, 420px)",
          marginLeft: "calc(min(60vw, 420px) / -2)",
          marginTop: "calc(min(60vw, 420px) / -2)",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,240,190,0.95) 0%, rgba(255,211,106,0.45) 35%, rgba(255,211,106,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Lock coin, centred on the seam. The wrapper does the centring so the
          animated transform below never fights it. */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          // The artwork's coin fills ~34% of the canvas.
          width: "min(78vw, 560px)",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        <motion.img
          src={lockImage}
          alt=""
          draggable={false}
          initial={false}
          animate={
            phase === "closing"
              ? { opacity: 1, scale: [0.3, 1.08, 1], rotate: [-200, 10, 0] }
              : phase === "closed"
                ? { opacity: 1, scale: [1, 1.04, 1], rotate: 0 }
                : phase === "unlocking"
                  ? { opacity: 1, scale: [1, 0.92, 1.18, 1.1], rotate: [0, 0, 180, 180] }
                  : phase === "opening"
                    ? { opacity: 0, scale: 1.5, rotate: 180 }
                    : { opacity: 0, scale: 0.3, rotate: -200 }
          }
          transition={
            phase === "closing"
              ? { duration: 0.55, delay: 0.12, ease: ease.out }
              : phase === "closed"
                ? { duration: 1.4, repeat: Infinity, ease: "easeInOut" }
                : phase === "unlocking"
                  ? { duration: UNLOCK_MS / 1000, times: [0, 0.2, 0.65, 1], ease: "easeInOut" }
                  : phase === "opening"
                    ? { duration: 0.25, ease: "easeOut" }
                    : { duration: 0 }
          }
          style={{
            display: "block",
            width: "100%",
            filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.55)) drop-shadow(0 0 18px rgba(255,211,106,0.45))",
            willChange: "transform, opacity",
          }}
        />
      </div>
    </div>
  );
}
